import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CurecallConfirmationModal,
  CurecallTextButton,
  UnuploadIcon,
  UploadFilledIcon,
} from '@honestica/curecall-ui'
import { useLocalizedNavigate } from 'src/hooks/use-localized-navigate'
import { useCurrentProject } from 'src/providers/current-project.provider'

type TChatbotPublishButtonProps = {
  isChatbotPublished: boolean
  hasUnsavedChanges?: boolean
  onPublish: () => Promise<void>
  onUnpublish: () => Promise<void>
  followupId: number
}

export function ChatbotPublishButton({
  isChatbotPublished,
  hasUnsavedChanges = false,
  onPublish,
  onUnpublish,
  followupId,
}: TChatbotPublishButtonProps) {
  const { t } = useTranslation()
  const navigate = useLocalizedNavigate()
  const { project } = useCurrentProject()
  const [isPublishModalOpen, setIsPublishModalOpen] = useState(false)
  const [isUnpublishModalOpen, setIsUnpublishModalOpen] = useState(false)

  const handlePublishClick = () => {
    setIsPublishModalOpen(true)
  }

  const handleUnpublishClick = () => {
    setIsUnpublishModalOpen(true)
  }

  const handleConfirmPublish = async () => {
    await onPublish()
    setIsPublishModalOpen(false)
    navigate(`/${project.id}/configuration/followups/${followupId}`)
  }

  const handleConfirmUnpublish = async () => {
    await onUnpublish()
    setIsUnpublishModalOpen(false)
  }

  return (
    <>
      <div className="c-flex c-gap-2">
        <CurecallTextButton
          type="button"
          iconLeft={<UploadFilledIcon className="c-h-5 c-mb-1" />}
          onClick={handlePublishClick}
          isDisabled={isChatbotPublished && !hasUnsavedChanges}
          className="c-w-max c-text-sm"
        >
          {t('pages.configuration.followups.chatbot.publish.publish')}
        </CurecallTextButton>
        {isChatbotPublished && (
          <div className="c-flex c-border-l c-border-grey-400 c-pl-2">
            <CurecallTextButton
              type="button"
              iconLeft={<UnuploadIcon className="c-h-5 c-mb-1" />}
              onClick={handleUnpublishClick}
              className="c-w-max c-text-sm"
            >
              {t('pages.configuration.followups.chatbot.publish.unpublish')}
            </CurecallTextButton>
          </div>
        )}
      </div>

      <CurecallConfirmationModal
        isOpen={isPublishModalOpen}
        color="ALERT"
        onConfirm={handleConfirmPublish}
        onCancel={() => setIsPublishModalOpen(false)}
        title={t('pages.configuration.followups.chatbot.publish.confirmation-title')}
        cancelButtonLabel={t('common.actions.cancel')}
        confirmButtonLabel={t('pages.configuration.followups.chatbot.publish.confirmation-button')}
      >
        <span>{t('pages.configuration.followups.chatbot.publish.confirmation-message')}</span>
      </CurecallConfirmationModal>

      <CurecallConfirmationModal
        isOpen={isUnpublishModalOpen}
        color="ALERT"
        onConfirm={handleConfirmUnpublish}
        onCancel={() => setIsUnpublishModalOpen(false)}
        title={t('pages.configuration.followups.chatbot.publish.unpublish-confirmation-title')}
        cancelButtonLabel={t('common.actions.cancel')}
        confirmButtonLabel={t('pages.configuration.followups.chatbot.publish.unpublish-confirmation-button')}
      >
        <span>{t('pages.configuration.followups.chatbot.publish.unpublish-confirmation-message')}</span>
      </CurecallConfirmationModal>
    </>
  )
}
